
import { Whitelabel, whitelabels, NetworkPartnership } from '../../utils/whitelabels';
import { resolveCompanyBranding } from '@/utils/brandingResolver';
import CPLoader from '@/components/globals/CPLoader.vue';
import CPNetworkPartnership from '@/components/login/CPNetworkPartnership.vue';
import { Component, Vue, Watch } from 'vue-property-decorator';
import CPMissingDocumentsSummary from '@/components/globals/CPMissingDocumentSummary.vue';
import logger from '@/logger/logging-button';
import { ActionType } from '@/utils/logTypes';
import jwt_decode from 'jwt-decode';

interface AccountRelocation {
  newUsername: string;
  oldUsername: string;
  loginUrl: string;
  companyName: string;
  /** True when host already matches target WL — only remind them of the new username. */
  alreadyOnTargetHost: boolean;
}

@Component({
  components: {
    CPLoader,
    CPMissingDocumentsSummary,
    CPNetworkPartnership,
  },
})
export default class CPLogin extends Vue {
  public email: string = '';
  public password: string = '';

  public errorEmail: string | null = null;
  public errorPassword: string | null = null;

  public loading: boolean = false;
  public relocation: AccountRelocation | null = null;
  private suppressAutoNavigate: boolean = false;
  /** JWT held while same-host alias notice is shown — used by Continue. */
  private pendingRelocationToken: string | null = null;
  private pendingTypedUsername: string = '';

  get subdomain() {
    return this.$store.state.subdomain;
  }

  public whitelabel: Whitelabel = whitelabels[0];
  public networkPartnership: NetworkPartnership | null = null;

  get isLoading() {
    return this.$store.state.isLoading;
  }
  get token() {
    return this.$store.state.account.token;
  }

  get portalLoginHref(): string {
    return this.buildPortalLoginUrl(
      this.relocation?.loginUrl,
      this.relocation?.newUsername,
    );
  }

  @Watch('subdomain')
  public async updateWhitelabel() {
    if (!this.subdomain) {
      this.whitelabel = whitelabels[0];
      this.networkPartnership = null;
    } else {
      const companyIndex = whitelabels
        .map((x) => x.name())
        .indexOf(this.subdomain);
      const selectedWhitelabel = whitelabels[companyIndex];
      this.whitelabel = selectedWhitelabel;
      this.networkPartnership = (
        await resolveCompanyBranding(this.subdomain, selectedWhitelabel)
      ).networkPartnership;
    }
  }

  @Watch('token')
  public isLoggedIn() {
    this.checkToken();
  }

  public checkToken() {
    if (this.suppressAutoNavigate || this.relocation) {
      return;
    }
    if (this.token && this.token.length > 0) {
      this.$router.push('/');
    }
  }

  private readRelocationFromToken(
    token: string,
    typedUsername: string,
    hostCode: string | undefined,
  ): AccountRelocation | null {
    try {
      const claims: any = jwt_decode(token);
      const companyClaim = String(claims.company || '');
      const companyParts = companyClaim.split(':');
      const companyCode = (companyParts[0] || '').toLowerCase().substring(0, 3);
      const companyName =
        companyParts.length > 1
          ? companyParts.slice(1, -1).join(':') || companyParts[1]
          : companyCode.toUpperCase();
      const newUsername = String(claims.accountNo || '').toUpperCase();
      const loginUrl = String(claims.loginUrl || '').trim();
      const relocatedFrom = String(claims.relocatedFrom || typedUsername || '')
        .trim()
        .toUpperCase();

      const hostMismatch =
        !!hostCode && !!companyCode && companyCode !== hostCode;
      const aliasUsed = !!claims.relocatedFrom;

      if (!hostMismatch && !aliasUsed) {
        return null;
      }
      if (!newUsername) {
        return null;
      }

      return {
        newUsername,
        oldUsername: relocatedFrom,
        loginUrl,
        companyName: companyName || companyCode.toUpperCase(),
        // On the correct portal with an old alias — don't send them "elsewhere".
        alreadyOnTargetHost: aliasUsed && !hostMismatch,
      };
    } catch {
      return null;
    }
  }

  public async login() {
    const details = {
      UserName: this.email,
      Password: this.password,
    };
    this.loading = true;
    this.relocation = null;
    if (!this.email) {
      this.errorEmail = 'Please enter your username';
      logger.loggingButtonWarn(
        'click',
        ActionType.LOGIN,
        'login',
        this.errorEmail,
      );
    }
    if (!this.password) {
      this.errorPassword = 'Please enter your password';
      logger.loggingButtonWarn(
        'click',
        ActionType.LOGIN,
        'login',
        this.errorPassword,
      );
    }
    if (!this.errorEmail && !this.errorPassword) {
      const activeSubdomain = whitelabels.filter(
        (whitelabel: Whitelabel) => whitelabel.name() === this.subdomain,
      )[0];
      const hostCode = activeSubdomain?.code?.toLowerCase().substring(0, 3);
      // Allow host-code mismatch so Auth can resolve LoginUsernameAlias.
      this.suppressAutoNavigate = true;
      const result = await this.$store.dispatch('login', {
        ...details,
        hostCode,
      });
      const token = (result && result.token) as string | null;

      if (token) {
        const relocation = this.readRelocationFromToken(
          token,
          this.email,
          hostCode,
        );
        if (relocation || result.relocated || result.hostMismatch) {
          this.relocation =
            relocation ||
            this.readRelocationFromToken(token, this.email, hostCode);
          this.pendingRelocationToken = token;
          this.pendingTypedUsername = this.email;
          // Session must not be hydrated for wrong-host / alias notice paths.
          if (this.$store.state.account?.token) {
            await this.$store.dispatch('setToken', '');
            await this.$store.dispatch('setUsername', '');
          }
          logger.loggingButtonWarn(
            'click',
            ActionType.LOGIN,
            'login',
            this.relocation?.alreadyOnTargetHost
              ? `Alias login on target host — remind new username ${this.relocation?.newUsername}`
              : `Account belongs on ${this.relocation?.companyName || 'another'} portal`,
          );
          this.loading = false;
          this.suppressAutoNavigate = false;
          return;
        }
        this.suppressAutoNavigate = false;
        this.checkToken();
        logger.loggingButton('click', ActionType.LOGIN, 'login');
      } else {
        this.suppressAutoNavigate = false;
        logger.loggingButtonError(
          'click',
          ActionType.LOGIN,
          'login',
          'Login failed',
        );
      }
    }
    this.loading = false;
  }

  public dismissRelocation() {
    this.relocation = null;
    this.pendingRelocationToken = null;
    this.pendingTypedUsername = '';
    this.password = '';
  }

  public useNewUsernameOnThisPortal() {
    if (!this.relocation?.newUsername) {
      return;
    }
    this.email = this.relocation.newUsername;
    this.password = '';
    this.relocation = null;
    this.pendingRelocationToken = null;
    this.pendingTypedUsername = '';
  }

  public goToNewPortal() {
    const href = this.portalLoginHref;
    if (!href) {
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  public async copyText(value: string) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* ignore */
    }
  }

  private buildPortalLoginUrl(
    loginUrl?: string,
    username?: string,
  ): string {
    if (!loginUrl) {
      return '';
    }
    const trimmedUser = (username || '').trim();
    try {
      const url = new URL(loginUrl, window.location.origin);
      if (trimmedUser) {
        url.searchParams.set('username', trimmedUser);
      }
      return url.toString();
    } catch {
      if (!trimmedUser) {
        return loginUrl;
      }
      const sep = loginUrl.includes('?') ? '&' : '?';
      return `${loginUrl}${sep}username=${encodeURIComponent(trimmedUser)}`;
    }
  }

  private applyUsernameFromQuery() {
    const raw = this.$route.query.username;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value === 'string' && value.trim()) {
      this.email = value.trim().toUpperCase();
    }
  }

  public mounted() {
    this.checkToken();
    this.updateWhitelabel();
    this.applyUsernameFromQuery();
  }
}
